import { useState, type ReactNode } from "react";
import { Caption1, Card, makeStyles, Popover, PopoverSurface, PopoverTrigger, tokens } from "@fluentui/react-components";
import { Add16Regular } from "@fluentui/react-icons";
import { FuiButton } from "@/components/fui-control";
import {
  FilterBuilder,
  FilterChipBar,
  createFilterClause,
  taskFilterFields,
  type FilterClause,
  type FilterFieldDef,
} from "@/components/filter-builder";
import { SectionTitle } from "@/components/ui-typography";

const useStyles = makeStyles({
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  sectionHead: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  card: {
    padding: tokens.spacingHorizontalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  note: {
    color: tokens.colorNeutralForeground3,
  },
  frames: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  frame: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    maxWidth: "560px",
    padding: "4px",
    overflowX: "auto",
  },
  surface: {
    padding: "4px",
    width: "560px",
    maxWidth: "calc(100vw - 24px)",
  },
  gone: {
    color: tokens.colorNeutralForeground3,
  },
});

const ANY_CLAUSES: FilterClause[] = [
  { id: "any-status", fieldId: "status", op: "is", values: [] },
  { id: "any-priority", fieldId: "priority", op: "is", values: [] },
];

const VALUED_CLAUSES: FilterClause[] = [
  { id: "val-status", fieldId: "status", op: "is", values: ["inProgress"] },
  { id: "val-assignee", fieldId: "assignee", op: "is", values: ["me", "kim", "lee"] },
];

const NEGATED_CLAUSES: FilterClause[] = [{ id: "not-status", fieldId: "status", op: "isNot", values: ["done"] }];

const FILLED_ROWS: FilterClause[] = [
  { id: "ex-status", fieldId: "status", op: "is", values: ["inProgress"] },
  { id: "ex-assignee", fieldId: "assignee", op: "is", values: [] },
];

function Section({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  const styles = useStyles();
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <SectionTitle>{title}</SectionTitle>
        <Caption1>{hint}</Caption1>
      </div>
      {children}
    </section>
  );
}

function copyClauses(clauses: FilterClause[]): FilterClause[] {
  return clauses.map((clause) => ({ ...clause, values: [...clause.values] }));
}

function ChipSample({ fields, initial }: { fields: FilterFieldDef[]; initial: FilterClause[] }) {
  const styles = useStyles();
  const [clauses, setClauses] = useState(() => copyClauses(initial));
  if (clauses.length === 0) {
    return <Caption1 className={styles.gone}>조건을 모두 지우면 이 줄은 없습니다.</Caption1>;
  }
  return (
    <FilterChipBar
      fields={fields}
      clauses={clauses}
      onRemove={(id) => setClauses((current) => current.filter((clause) => clause.id !== id))}
      onClear={() => setClauses([])}
      onAdd={() => setClauses((current) => [...current, createFilterClause("status")])}
    />
  );
}

function EmptyBuilder({ fields }: { fields: FilterFieldDef[] }) {
  const [clauses, setClauses] = useState<FilterClause[]>([]);
  return <FilterBuilder fields={fields} clauses={clauses} onChange={setClauses} />;
}

function FilledBuilder({ fields }: { fields: FilterFieldDef[] }) {
  const [clauses, setClauses] = useState(() => copyClauses(FILLED_ROWS));
  const [openId, setOpenId] = useState<string | null>("ex-status");
  return <FilterBuilder fields={fields} clauses={clauses} onChange={setClauses} openClauseId={openId} onOpenClauseId={setOpenId} />;
}

function AddTrigger() {
  return (
    <PopoverTrigger disableButtonEnhancement>
      <FuiButton appearance="subtle" density="dialog" icon={<Add16Regular />}>
        추가
      </FuiButton>
    </PopoverTrigger>
  );
}

function LiveFilter({ fields }: { fields: FilterFieldDef[] }) {
  const styles = useStyles();
  const [clauses, setClauses] = useState<FilterClause[]>(() => copyClauses(ANY_CLAUSES));
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={(_, data) => setOpen(data.open)} positioning="below-start">
      {clauses.length > 0 ? (
        <FilterChipBar
          fields={fields}
          clauses={clauses}
          onRemove={(id) => setClauses((current) => current.filter((clause) => clause.id !== id))}
          onClear={() => setClauses([])}
          add={<AddTrigger />}
        />
      ) : (
        <AddTrigger />
      )}
      <PopoverSurface className={styles.surface}>
        <FilterBuilder fields={fields} clauses={clauses} onChange={setClauses} />
      </PopoverSurface>
    </Popover>
  );
}

export function FiltersTab() {
  const styles = useStyles();
  const fields = taskFilterFields();
  return (
    <>
      <Section title="칩 줄" hint="조건이 있을 때만 줄을 그립니다. 값이 없으면 전체이고, 둘이면 이름을 나열하고, 셋부터는 개수입니다.">
        <Card className={styles.card}>
          <Caption1 className={styles.note}>값이 전체</Caption1>
          <ChipSample fields={fields} initial={ANY_CLAUSES} />
          <Caption1 className={styles.note}>진행 중, 담당 셋</Caption1>
          <ChipSample fields={fields} initial={VALUED_CLAUSES} />
          <Caption1 className={styles.note}>아니다</Caption1>
          <ChipSample fields={fields} initial={NEGATED_CLAUSES} />
        </Card>
      </Section>
      <Section title="빌더" hint="조건이 없으면 안내 한 줄입니다. 값 버튼을 누르면 그 행 아래에 목록이 열립니다.">
        <div className={styles.frames}>
          <div className={styles.frame}>
            <EmptyBuilder fields={fields} />
          </div>
          <div className={styles.frame}>
            <FilledBuilder fields={fields} />
          </div>
        </div>
      </Section>
      <Section title="같이 쓰기" hint="추가가 조건 패널을 엽니다. 고른 값과 연산, 지우기는 칩에 바로 나타납니다.">
        <Card className={styles.card}>
          <LiveFilter fields={fields} />
        </Card>
      </Section>
    </>
  );
}
